import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DashboardLayout } from '@components/layout/DashboardLayout.tsx';
import {
  Box,
  CircularProgress,
  Alert,
  IconButton,
} from '@mui/material';

import { getClientDetails } from '@services/client.service';
import { ClientDetails } from '@/types/client';
import { useAuth } from '@/hooks/useAuth';
import { useAppDispatch, useAppSelector, selectClientBadges } from '@/store';
import { fetchClientBadges } from '@/store/client-badges/clientBadgesSlice';
import { RoleProtectedRoute } from '@components/RoleProtectedRoute.tsx';
import { ClientProfileSidebar, hasTabAccess } from '@/components/layout/ClientProfileSidebar';
import { PageHint } from '@/components/common/PageHint';
import { PageIndices } from '@/common/i-doc/i-doc.registry.ts';
import { ClientCalendarTab } from '@/components/clients/ClientCalendarTab';
import { ClientFilesTab } from '@/components/clients/ClientFilesTab';
import { ClientQuestionnairesTab } from '@/components/clients/ClientQuestionnairesTab';
import { ExpertClientQuestionnaireDetail } from '@/pages/expert/ClientQuestionnairePage';
import { ClientDashboardTab } from '@/components/clients/ClientDashboardTab';
import { ClientReportsTab } from '@/components/clients/ClientReportsTab';
import { ClientAppointmentsTab } from '@/components/clients/ClientAppointmentsTab';
import { ClientNotificationsTab } from '@/components/clients/ClientNotificationsTab';
import { ClientMailingsTab } from '@/components/clients/ClientMailingsTab';
import { ProPlanGate } from '@/components/common/ProPlanGate';
import { useExpertPlan } from '@/hooks/useExpertPlan';
import { UserAvatar } from '@/components/common/UserAvatar';
import { renderClientProfileSection } from '@/components/clients/renderClientProfileSection';

const tabConfig = [
  { path: 'dashboard',     index: 0,  labelKey: 'clientProfile.tab.dashboard' },
  { path: 'profile',       index: 1,  labelKey: 'clientProfile.tab.profile' },
  { path: 'calendar',      index: 2,  labelKey: 'clientProfile.tab.calendar' },
  { path: 'questionnaires', index: 5, labelKey: 'clientProfile.tab.questionnaires' },
  { path: 'files',         index: 6,  labelKey: 'clientProfile.tab.files' },
  { path: 'reports',       index: 8,  labelKey: 'clientProfile.tab.reports' },
  { path: 'appointments',  index: 9,  labelKey: 'clientProfile.tab.appointments' },
  { path: 'notifications', index: 10, labelKey: 'clientProfile.tab.notifications' },
  { path: 'mailings',      index: 11, labelKey: 'clientProfile.tab.mailings' },
];

const ClientPageContent: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { hasFeature, loading: planLoading } = useExpertPlan();
  const isSuperAdmin = (user?.roles || []).some(r => r.toLowerCase() === 'superadmin');
  const dispatch = useAppDispatch();
  const clientBadges = useAppSelector(selectClientBadges(Number(id)));
  const [client, setClient] = useState<ClientDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [questionnaireTitle, setQuestionnaireTitle] = useState<string | null>(null);

  const activeTabInfo = tabConfig.find(tab => location.pathname.includes(`/client/${id}/${tab.path}`));
  const activeTab = activeTabInfo?.index ?? 0;
  // Dashboard: edge-to-edge profile header (redesign); its own side padding is set by the tab.
  const isDashboardTab = activeTab === 0;
  const activeTabLabel = t(activeTabInfo?.labelKey ?? 'clientProfile.tab.dashboard');
  const isQuestionnaireDetail = /^\/client\/[^/]+\/questionnaires\/[^/]+$/.test(location.pathname);
  const breadcrumbTitle = isQuestionnaireDetail && questionnaireTitle
    ? `${t('clientProfile.title')} → ${activeTabLabel} → ${questionnaireTitle}`
    : `${t('clientProfile.title')} → ${activeTabLabel}`;

  useEffect(() => {
    if (!isQuestionnaireDetail) setQuestionnaireTitle(null);
  }, [isQuestionnaireDetail]);

  const handleTabChange = (index: number) => {
    const tab = tabConfig.find(t => t.index === index);
    if (tab) {
      navigate(`/client/${id}/${tab.path}`);
    }
  };

  useEffect(() => {
    const fetchClient = async () => {
      if (!id) {
        setError('Client ID is missing.');
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        setError(null);
        const response = await getClientDetails(Number(id));
        setClient(response);
      } catch (err: any) {
        const backendMessage = err.response?.data?.message;
        const errorMessage = Array.isArray(backendMessage) ? backendMessage.join(', ') : backendMessage || 'Не вдалося завантажити інформацію про клієнта.';
        setError(errorMessage);
        console.error('Failed to fetch client details', err);
      } finally {
        setLoading(false);
      }
    };

    fetchClient();
  }, [id]);

  useEffect(() => {
    if (id) dispatch(fetchClientBadges(Number(id)));
  }, [id, dispatch]);

  if (loading) {
    return (
      <DashboardLayout titleOverride={breadcrumbTitle}>
        <Box display="flex" justifyContent="center" sx={{ mt: 4 }}>
          <CircularProgress />
        </Box>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout titleOverride={breadcrumbTitle}>
        <Box sx={{ mt: 4 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      </DashboardLayout>
    );
  }

  if (!client) {
    return (
      <DashboardLayout titleOverride={breadcrumbTitle}>
        <Box sx={{ mt: 4 }}>
          <Alert severity="info">{t('clientProfile.notFound')}</Alert>
        </Box>
      </DashboardLayout>
    );
  }

  const permissions = client.accessPermissions ?? [];

  const guard = (tabIndex: number, element: React.ReactNode) =>
    isSuperAdmin || hasTabAccess(tabIndex, permissions) ? element : (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '40vh' }}>
        <Alert severity="warning">{t('clientProfile.accessDenied')}</Alert>
      </Box>
    );

  const profileContent = (
    <Box data-i-doc={PageIndices.EXPERT_CLIENT_PROFILE}>
      <PageHint iDoc={PageIndices.EXPERT_CLIENT_PROFILE} />
      {renderClientProfileSection(t('clientProfile.section.general'), client.general)}
      {renderClientProfileSection(t('clientProfile.section.nutrition'), client.nutrition, client.fieldLabels)}
      {renderClientProfileSection(t('clientProfile.section.medical'), client.medical, client.fieldLabels)}
      {renderClientProfileSection(t('clientProfile.section.social'), client.social, client.fieldLabels)}
    </Box>
  );

  const rightAction = (
    <IconButton 
      onClick={() => setMobileSidebarOpen(true)}
      color="inherit"
      sx={{ p: 0 }}
    >
      <UserAvatar 
        path={client.general.avatar} 
        name={client.general.name} 
        sx={{ width: 32, height: 32 }} 
      />
    </IconButton>
  );

  return (
    <DashboardLayout rightAction={rightAction} titleOverride={breadcrumbTitle} disableContentPadding>
      <Box sx={{
        pt: isDashboardTab ? 0 : 1,
        px: isDashboardTab ? 0 : 1.5,
        pb: isDashboardTab ? 0 : 1,
        mr: { xs: 0, md: '200px' },
        height: 'calc(100vh - 52px)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}>
        <Box sx={{ flexGrow: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
          <Routes>
            <Route index element={<ClientDashboardTab clientId={id!} client={client} />} />
            <Route path="dashboard" element={<ClientDashboardTab clientId={id!} client={client} />} />
            <Route path="profile" element={guard(1, profileContent)} />
            <Route path="calendar" element={guard(2, <ClientCalendarTab clientId={id!} clientTimezone={client?.general.timezone} />)} />
            {/* Денні норми перенесено в картку клієнта панелі продакта (/pm/client/:id/daily-norms). */}
            <Route path="goals" element={<Navigate to={`/client/${id}/dashboard`} replace />} />
            <Route path="questionnaires" element={guard(5, <ClientQuestionnairesTab clientId={id!} />)} />
            <Route path="questionnaires/:templateId" element={guard(5, <ExpertClientQuestionnaireDetail clientId={id!} onTitleChange={setQuestionnaireTitle} />)} />
            <Route path="files" element={guard(6, <ClientFilesTab clientId={id!} />)} />
            <Route path="reports" element={<ClientReportsTab clientId={id!} />} />
            <Route path="appointments" element={<ClientAppointmentsTab clientId={id!} />} />
            <Route path="notifications" element={<ClientNotificationsTab clientId={id!} />} />
            <Route path="mailings" element={<><PageHint iDoc={PageIndices.EXPERT_CLIENT_MAILINGS} /><ProPlanGate hasAccess={hasFeature('client_mailings')} loading={planLoading}><ClientMailingsTab clientId={id!} /></ProPlanGate></>} />
          </Routes>
        </Box>
        <ClientProfileSidebar
          clientId={Number(id)}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          clientGeneralInfo={client.general}
          accessPermissions={permissions}
          groups={client.groups}
          fullAccess={isSuperAdmin}
          isMobileOpen={mobileSidebarOpen}
          onClose={() => setMobileSidebarOpen(false)}
          badges={clientBadges ? {
            2: clientBadges.calendar,
            5: clientBadges.questionnaires,
            6: clientBadges.files,
            9: clientBadges.appointments,
          } : {}}
        />
      </Box>
    </DashboardLayout>
  );
};

const ClientPage: React.FC = () => {
  return (
    <RoleProtectedRoute allowedRoles={['expert', 'superadmin']}>
      <ClientPageContent />
    </RoleProtectedRoute>
  );
};

export default ClientPage;
